/**
 * CompletenessGate — the Phase-1 "encode everything" completeness gate.
 * Shown in the step panel whenever a Phase-2 (Freeze & Count) step is opened
 * before all 6 Phase-1 category steps are complete. Lists each Phase-1 step
 * with its status and a jump link; HARD-blocks the physical count until 6/6.
 * (Soft during entry — you can still revisit Phase-1 steps — hard at finalize.)
 */
import { CheckCircle2, Circle, Lock, ArrowRight, ShieldCheck } from 'lucide-react';

export default function CompletenessGate({ phase1Steps, sessionSteps, onJump }) {
  const done = phase1Steps.filter(s => sessionSteps[s.key]?.status === 'complete');
  const total = phase1Steps.length;
  const count = done.length;
  const complete = total > 0 && count === total;
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const firstPending = phase1Steps.find(s => sessionSteps[s.key]?.status !== 'complete');

  return (
    <div className="flex flex-col items-center text-center py-6" data-testid="completeness-gate">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${complete ? 'bg-emerald-50' : 'bg-amber-50'}`}>
        {complete ? <ShieldCheck size={22} className="text-emerald-600" /> : <Lock size={20} className="text-amber-500" />}
      </div>
      <h4 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
        {complete ? 'Phase 1 complete — Freeze & Count unlocked' : 'Finish encoding before the physical count'}
      </h4>
      <p className="text-xs text-slate-500 max-w-md mt-1">
        {complete
          ? 'Every record type is reconciled/verified. You can now close the shift and freeze the snapshot.'
          : 'The physical count is locked until all 6 record types are reconciled or verified — so expected balances are complete before you count.'}
      </p>

      {/* Progress */}
      <div className="w-full max-w-sm mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-slate-600" data-testid="completeness-count">Phase 1: {count}/{total} encoded</span>
          <span className={`text-[11px] font-bold ${complete ? 'text-emerald-600' : 'text-amber-600'}`}>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full transition-all ${complete ? 'bg-emerald-600' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} data-testid="completeness-bar" />
        </div>
      </div>

      {/* Per-step checklist */}
      <div className="w-full max-w-sm mt-4 space-y-1">
        {phase1Steps.map(s => {
          const isDone = sessionSteps[s.key]?.status === 'complete';
          return (
            <button key={s.key} onClick={() => onJump?.(s.key)} data-testid={`completeness-jump-${s.key}`}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-100 hover:border-[#1A4D2E]/30 transition-colors text-left">
              {isDone ? <CheckCircle2 size={15} className="text-emerald-600 shrink-0" /> : <Circle size={15} className="text-slate-300 shrink-0" />}
              <span className={`text-sm flex-1 ${isDone ? 'text-slate-500' : 'text-slate-700 font-medium'}`}>{s.title}</span>
              {!isDone && <ArrowRight size={13} className="text-slate-300" />}
            </button>
          );
        })}
      </div>

      {!complete && firstPending && (
        <button onClick={() => onJump?.(firstPending.key)} data-testid="completeness-goto-next"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A4D2E] hover:underline">
          Continue with “{firstPending.title}” <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}
